import React, { useState, useEffect, useRef } from 'react';
import MainLayout from '../../components/Layouts/Main/MainLayout.index';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Form, Row, Col, Card } from 'react-bootstrap';
import { FaPlus, FaTrash, FaDownload, FaUpload, FaImage } from 'react-icons/fa';
import { parseBannersFromHtml, generateBannerHtml } from '../../utils/bannerUtils';

export default function BannerManage() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [uploadedImages, setUploadedImages] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentEditingIndex, setCurrentEditingIndex] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const userId = sessionStorage.getItem('userId');
    if (!userId) {
      navigate('/');
      return;
    }
    loadCurrentBanners();
  }, [navigate]);

  const loadCurrentBanners = async () => {
    try {
      const response = await fetch('/banners.html');
      const text = await response.text();
      const parsed = parseBannersFromHtml(text);
      setBanners(parsed);
    } catch (error) {
      console.error('Failed to load banners.html:', error);
      alert('banners.htmlの読み込みに失敗しました');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const parsed = parseBannersFromHtml(event.target.result);
          setBanners(parsed);
          alert(`${parsed.length}件のバナーを読み込みました`);
        } catch (error) {
          alert('HTMLファイルの解析に失敗しました');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file || currentEditingIndex === null) return;

    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      const fileName = `pic/banner_${Date.now()}_${file.name}`;
      
      setUploadedImages(prev => ({
        ...prev,
        [currentEditingIndex]: {
          name: fileName,
          data: base64
        }
      }));

      updateBanner(currentEditingIndex, 'src', fileName);
      updateBanner(currentEditingIndex, '_base64', base64);
      
      alert(`画像「${file.name}」をアップロードしました`);
      setShowUploadModal(false);
    };
    reader.readAsDataURL(file);
  };

  const openUploadModal = (index) => {
    setCurrentEditingIndex(index);
    setShowUploadModal(true);
  };

  const updateBanner = (index, field, value) => {
    setBanners(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addBanner = () => {
    const newIndex = banners.length;
    const newBanner = {
      id: `big_${String(newIndex + 1).padStart(2, '0')}_new`,
      index: newIndex,
      href: 'https://example.com',
      src: 'pic/new_banner.jpg',
      alt: `バナー${newIndex + 1}`
    };
    setBanners([...banners, newBanner]);
    alert('新しいバナーを追加しました');
  };

  const removeBanner = (index) => {
    if (banners.length <= 1) {
      alert('最後のバナーは削除できません');
      return;
    }
    setBanners(banners.filter((_, i) => i !== index));
    setUploadedImages(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
    alert('バナーを削除しました');
  };

  const moveBanner = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === banners.length - 1) return;

    setBanners(prev => {
      const updated = [...prev];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
      return updated;
    });
  };

  const generateHtml = () => {
    let html = generateBannerHtml(banners);
    
    if (Object.keys(uploadedImages).length > 0) {
      const imageFiles = [];
      Object.values(uploadedImages).forEach(img => {
        if (img.data) {
          imageFiles.push({ name: img.name, data: img.data });
        }
      });

      if (imageFiles.length > 0) {
        const imgDataJson = JSON.stringify(imageFiles);
        const imgDataBase64 = btoa(encodeURIComponent(imgDataJson));
        
        html = html.replace('</body>', `
  <script>
    window.addEventListener('load', function() {
      try {
        var imgData = JSON.parse(decodeURIComponent(atob('${imgDataBase64}')));
        imgData.forEach(function(item) {
          var img = document.querySelector('img[src="' + item.name + '"]');
          if (img) {
            img.src = item.data;
          }
        });
      } catch(e) { console.error(e); }
    });
  </script>
</body>`);
      }
    }

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'banners_new.html';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    if (Object.keys(uploadedImages).length > 0) {
      alert('HTMLファイルと画像データをダウンロードしました');
      
      const zip = new Blob([JSON.stringify(Object.values(uploadedImages), null, 2)], { type: 'application/json' });
      const zipUrl = URL.createObjectURL(zip);
      const zipLink = document.createElement('a');
      zipLink.href = zipUrl;
      zipLink.download = 'images_data.json';
      document.body.appendChild(zipLink);
      zipLink.click();
      document.body.removeChild(zipLink);
      URL.revokeObjectURL(zipUrl);
      alert('※画像データはimages_data.jsonにも保存されました。サーバーにはpicフォルダに画像をアップロードしてください。');
    } else {
      alert('HTMLファイルをダウンロードしました');
    }
  };

  const previewHtml = () => {
    let html = generateBannerHtml(banners);
    
    if (Object.keys(uploadedImages).length > 0) {
      html = html.replace('</body>', `
  <script>
    window.addEventListener('load', function() {
      try {
        var imgData = ${JSON.stringify(Object.values(uploadedImages).map(img => ({ name: img.name, data: img.data })))};
        imgData.forEach(function(item) {
          var img = document.querySelector('img[src="' + item.name + '"]');
          if (img) {
            img.src = item.data;
          }
        });
      } catch(e) { console.error(e); }
    });
  </script>
</body>`);
    }

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  return (
    <MainLayout>
      <div className="container-fluid">
        <h4 className="mb-4">バナー管理</h4>
        
        <Card className="mb-4">
          <Card.Header>HTMLファイル読み込み</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>既存のbanners.htmlを選択:</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".html"
                    onChange={handleFileUpload}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="d-flex align-items-center">
                <Button variant="info" onClick={previewHtml} className="me-2">
                  <FaUpload className="me-1" /> プレビュー
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>バナー一覧 ({banners.length}件)</span>
            <Button variant="primary" size="sm" onClick={addBanner}>
              <FaPlus className="me-1" /> バナー追加
            </Button>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>順序</th>
                  <th style={{ width: '150px' }}>ID</th>
                  <th style={{ width: '120px' }}>画像</th>
                  <th>画像URL (src)</th>
                  <th>リンクURL (href)</th>
                  <th>Altテキスト</th>
                  <th style={{ width: '100px' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {banners.map((banner, index) => (
                  <tr key={index}>
                    <td>
                      <div className="d-flex flex-column">
                        <Button 
                          variant="link" 
                          size="sm" 
                          onClick={() => moveBanner(index, 'up')}
                          disabled={index === 0}
                          className="p-0"
                        >▲</Button>
                        <Button 
                          variant="link" 
                          size="sm" 
                          onClick={() => moveBanner(index, 'down')}
                          disabled={index === banners.length - 1}
                          className="p-0"
                        >▼</Button>
                      </div>
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={banner.id}
                        onChange={(e) => updateBanner(index, 'id', e.target.value)}
                        size="sm"
                      />
                    </td>
                    <td>
                      <div style={{ width: '100px', height: '50px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: '4px' }}>
                        {uploadedImages[index] ? (
                          <img 
                            src={uploadedImages[index].data} 
                            alt="preview" 
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          />
                        ) : banner.src && (banner.src.startsWith('http') || banner.src.startsWith('data:')) ? (
                          <img 
                            src={banner.src} 
                            alt="preview" 
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          />
                        ) : (
                          <FaImage style={{ color: '#ccc' }} />
                        )}
                      </div>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        block
                        onClick={() => openUploadModal(index)}
                        className="mt-1"
                      >
                        アップロード
                      </Button>
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={banner.src}
                        onChange={(e) => updateBanner(index, 'src', e.target.value)}
                        size="sm"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={banner.href}
                        onChange={(e) => updateBanner(index, 'href', e.target.value)}
                        size="sm"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={banner.alt}
                        onChange={(e) => updateBanner(index, 'alt', e.target.value)}
                        size="sm"
                      />
                    </td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removeBanner(index)}
                        disabled={banners.length <= 1}
                        block
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Row className="mb-4">
          <Col className="text-center">
            <Button variant="success" size="lg" onClick={generateHtml}>
              <FaDownload className="me-2" />
              HTML生成・ダウンロード
            </Button>
          </Col>
        </Row>

        <Card>
          <Card.Header>使い方</Card.Header>
          <Card.Body>
            <ol>
              <li>上の「ファイルを選択」から既存のbanners.htmlを読み込む</li>
              <li>「アップロード」ボタンから画像を直接アップロード可能（またはURLを入力）</li>
              <li>▲▼ボタンでバナーの順序を変更できます</li>
              <li>画像URLとリンクURLを必要に応じて編集</li>
              <li>「HTML生成・ダウンロード」ボタンをクリック</li>
              <li>画像を服务器のpicフォルダにアップロード（画像データ場合はimages_data.jsonを確認）</li>
            </ol>
          </Card.Body>
        </Card>

        {showUploadModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999
          }} onClick={() => setShowUploadModal(false)}>
            <div style={{
              background: 'white', padding: '20px', borderRadius: '8px', maxWidth: '400px', width: '90%'
            }} onClick={e => e.stopPropagation()}>
              <h5>画像アップロード</h5>
              <p className="text-muted">バナー #{currentEditingIndex + 1} の画像をアップロード</p>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                ref={fileInputRef}
              />
              <div className="mt-3 d-flex justify-content-end">
                <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
                  キャンセル
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
